import React, { useState, useEffect } from "react";
import axios from "axios";
import { Toaster, toast } from "sonner";
import "@/App.css";
import { VIDEOS } from "@/klaren/assets";
import Nav from "@/klaren/Nav";
import Hero from "@/klaren/Hero";
import VideoBand from "@/klaren/VideoBand";
import { Intro, Range, Philosophy, LeaveOut, Footer } from "@/klaren/Sections";
import { ShopPage, ProductPage, CartDrawer } from "@/klaren/Shop";
import KlarenEnding from "@/klaren/KlarenEnding";
import StickyCTA from "@/klaren/StickyCTA";
import CheckoutReturn from "@/klaren/CheckoutReturn";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SCROLL_IDS = ["intro", "range", "philosophy", "promise", "contact"];
const TOAST_OPTS = { style: { background: "var(--paper-2)", border: "1px solid var(--line)", color: "var(--ink)", fontFamily: "var(--font-sans)" } };

function App() {
  const [route, setRoute] = useState("home");
  const [products, setProducts] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState(() => {
    try { return JSON.parse(localStorage.getItem("klaren_cart") || "{}"); } catch { return {}; }
  });
  const [cartOpen, setCartOpen] = useState(false);
  const [checkoutBusy, setCheckoutBusy] = useState(false);
  const [returnSession, setReturnSession] = useState(null);

  useEffect(() => {
    axios.get(`${API}/products`)
      .then((r) => setProducts(r.data))
      .catch(() => toast.error("Could not load the range. Please refresh."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    try { localStorage.setItem("klaren_cart", JSON.stringify(cart)); } catch { /* ignore */ }
  }, [cart]);

  // Detect return from Stripe checkout
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("session_id");
    const cancelled = params.get("checkout");
    if (sid) {
      setReturnSession(sid);
      setRoute("checkout");
    } else if (cancelled === "cancelled") {
      window.history.replaceState({}, "", window.location.pathname);
      toast("Checkout cancelled", { description: "Your bag is saved — pick up where you left off." });
      setTimeout(() => setCartOpen(true), 400);
    }
  }, []);

  const cartCount = Object.values(cart).reduce((a, b) => a + b, 0);
  const add = (id) => { setCart((c) => ({ ...c, [id]: (c[id] || 0) + 1 })); setCartOpen(true); };
  const setQty = (id, q) => setCart((c) => ({ ...c, [id]: Math.max(0, q) }));

  const go = (r) => {
    if (SCROLL_IDS.includes(r)) {
      const scroll = () => { const el = document.getElementById(r); if (el) el.scrollIntoView({ behavior: "smooth", block: "start" }); };
      if (route !== "home") { setRoute("home"); setTimeout(scroll, 140); } else scroll();
      return;
    }
    setRoute(r);
    window.scrollTo({ top: 0, behavior: "instant" });
  };
  const open = (p) => { setActive(p); setRoute("product"); window.scrollTo({ top: 0, behavior: "instant" }); };

  const doCheckout = async () => {
    const items = Object.entries(cart).filter(([, q]) => q > 0).map(([id, q]) => ({ id, qty: q }));
    if (!items.length) { toast.error("Your bag is empty."); return; }
    try {
      setCheckoutBusy(true);
      const { data } = await axios.post(`${API}/checkout/session`, { items, origin_url: window.location.origin });
      if (data && data.url) { window.location.href = data.url; }
      else { throw new Error("No checkout URL"); }
    } catch (e) {
      setCheckoutBusy(false);
      toast.error("Could not start checkout. Please try again.");
    }
  };

  const onPaid = () => { setCart({}); try { localStorage.removeItem("klaren_cart"); } catch { /* ignore */ } };
  const leaveCheckout = () => {
    window.history.replaceState({}, "", window.location.pathname);
    setReturnSession(null);
    setRoute("home");
    window.scrollTo({ top: 0, behavior: "instant" });
  };
  const checkoutToShop = () => {
    window.history.replaceState({}, "", window.location.pathname);
    setReturnSession(null);
    setRoute("shop");
    window.scrollTo({ top: 0, behavior: "instant" });
  };

  if (route === "checkout" && returnSession) {
    return (
      <div className="klaren-root" style={{ minHeight: "100vh" }}>
        <Toaster position="top-center" toastOptions={TOAST_OPTS} />
        <CheckoutReturn sessionId={returnSession} api={API} onPaid={onPaid} onContinue={leaveCheckout} onShop={checkoutToShop} />
      </div>
    );
  }

  const showSticky = (route === "home" || route === "product") && !cartOpen;

  return (
    <div className="klaren-root" style={{ position: "relative", minHeight: "100vh" }}>
      <Toaster position="top-center" toastOptions={TOAST_OPTS} />

      <Nav route={route} onNav={go} cartCount={cartCount} onCart={() => setCartOpen(true)} />

      {route === "home" && (
        <main>
          <Hero onShop={() => go("shop")} onPhilosophy={() => go("philosophy")} />
          <Intro />
          <VideoBand
            src={VIDEOS.touch}
            eyebrow="Skin-kind thinking"
            title={<>What touches your home<br />touches <span className="k-serif-i">you</span> too.</>}
            subtext="Choose skin-kind care."
            testId="videoband-touch"
          />
          <Range products={products} loading={loading} onOpen={open} onAdd={add} />
          <VideoBand
            src={VIDEOS.leaveBehind}
            title={<>You don’t just clean your home.<br />You <span className="k-serif-i">live</span> in what’s left behind.</>}
            height="92vh"
            titleSize="clamp(30px,4.8vw,68px)"
            scrim="linear-gradient(180deg, rgba(14,12,10,.5), rgba(14,12,10,.34) 45%, rgba(14,12,10,.66))"
            testId="videoband-leave"
          />
          <Philosophy />
          <LeaveOut />
        </main>
      )}
      {route === "shop" && <ShopPage products={products} loading={loading} onOpen={open} onAdd={add} />}
      {route === "product" && active && <ProductPage p={active} onAdd={add} onBack={() => go("shop")} />}
      {route === "product" && !active && <ShopPage products={products} loading={loading} onOpen={open} onAdd={add} />}

      {route !== "product" && <KlarenEnding onShop={() => go("shop")} />}
      {route !== "product" && <Footer onNav={go} />}

      <CartDrawer open={cartOpen} items={cart} products={products} onClose={() => setCartOpen(false)} onQty={setQty} onCheckout={doCheckout} checkoutBusy={checkoutBusy} />
      <StickyCTA show={showSticky} onShop={() => go("shop")} />
    </div>
  );
}

export default App;
